import { EyeIcon, Phone } from "@/components/icons";
import { Badge, Button, Input } from "@/components/kit";
import type { OrderListItemDTO } from "@/modules/orders/domain/types";
import {
  DELIVERY_METHOD_META,
  formatDeliverySummary,
  getOrderStatusMeta,
  PAYMENT_TYPE_META,
  orderStatusLabel,
} from "@/modules/orders/presentation/ui/order-status";
import { formatLak } from "@/shared/lib/currency";
import { useState } from "react";

type OrderKanbanCardProps = {
  order: OrderListItemDTO;
  canManage: boolean;
  onView: (id: string) => void;
  onMarkReady?: (id: string) => void;
  onMarkShipped?: (id: string, trackingNumber: string | null) => void;
  onMarkDelivered?: (id: string) => void;
  onMarkPickedUp?: (id: string) => void;
  isMarkReadyPending?: boolean;
  isMarkShippedPending?: boolean;
  isMarkDeliveredPending?: boolean;
  isMarkPickedUpPending?: boolean;
};

export function OrderKanbanCard({
  order,
  canManage,
  onView,
  onMarkReady,
  onMarkShipped,
  onMarkDelivered,
  onMarkPickedUp,
  isMarkReadyPending,
  isMarkShippedPending,
  isMarkDeliveredPending,
  isMarkPickedUpPending,
}: OrderKanbanCardProps) {
  const [tracking, setTracking] = useState(order.trackingNumber ?? "");
  const statusMeta = getOrderStatusMeta(order.status, order.paymentType);

  return (
    <article className="brand-card flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-base text-foreground">
            {order.customerName}
          </p>
          {order.customerPhone ? (
            <p className="flex items-center gap-1 text-muted-foreground text-xs">
              <Phone className="size-3.5 shrink-0" />
              {order.customerPhone}
            </p>
          ) : null}
          <p className="mt-1 font-mono text-muted-foreground text-xs">
            {order.orderNumber}
          </p>
        </div>
        <span className="shrink-0 font-display text-foreground tabular-nums">
          {formatLak(order.total)}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Badge variant={statusMeta.variant}>
          {orderStatusLabel(order.status, order.paymentType)}
        </Badge>
        <Badge variant={PAYMENT_TYPE_META[order.paymentType].variant}>
          {PAYMENT_TYPE_META[order.paymentType].shortLabel}
        </Badge>
        <Badge variant={DELIVERY_METHOD_META[order.deliveryMethod].variant}>
          {DELIVERY_METHOD_META[order.deliveryMethod].shortLabel}
        </Badge>
      </div>

      <div className="text-muted-foreground text-xs leading-relaxed">
        <p>{order.itemCount} ລາຍການ</p>
        <p className="truncate">{formatDeliverySummary(order)}</p>
        {order.trackingNumber ? (
          <p className="font-mono">Tracking: {order.trackingNumber}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 border-secondary/15 border-t pt-3">
        {canManage && order.status === "ready_to_ship" ? (
          <Input
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && onMarkShipped) {
                e.preventDefault();
                onMarkShipped(order.id, tracking.trim() || null);
              }
            }}
            placeholder="ເລກ Tracking..."
            autoComplete="off"
            className="h-11"
          />
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="min-h-10 rounded-full"
            onClick={() => onView(order.id)}
          >
            <EyeIcon data-icon="inline-start" />
            ລາຍລະອຽດ
          </Button>
          {canManage && order.status === "paid" && onMarkReady ? (
            <Button
              size="sm"
              className="min-h-10 flex-1 rounded-full"
              isLoading={isMarkReadyPending}
              onClick={() => onMarkReady(order.id)}
            >
              ແພັກເສັດ
            </Button>
          ) : null}
          {canManage && order.status === "ready_to_ship" && onMarkShipped ? (
            <Button
              size="sm"
              className="min-h-10 flex-1 rounded-full"
              isLoading={isMarkShippedPending}
              onClick={() =>
                onMarkShipped(order.id, tracking.trim() || null)
              }
            >
              ສົ່ງແລ້ວ
            </Button>
          ) : null}
          {canManage && order.status === "shipped" && onMarkDelivered ? (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              isLoading={isMarkDeliveredPending}
              onClick={() => onMarkDelivered(order.id)}
            >
              ຂົນສົ່ງສົ່ງຮອດ
            </Button>
          ) : null}
          {canManage && (order.status === "shipped" || order.status === "delivered") && onMarkPickedUp ? (
            <Button
              size="sm"
              className="rounded-full"
              isLoading={isMarkPickedUpPending}
              onClick={() => onMarkPickedUp(order.id)}
            >
              ລູກຄ້າໄປເອົາແລ້ວ
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
